import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { useContext, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useRegistersListQuery } from '@/modules/common/queries/registers.ts';
import RegisterItem from '@/modules/admin-dashboard/components/items/RegisterItem.tsx';
import CreateRegisterModal from '@/modules/admin-dashboard/components/modals/CreateRegisterModal.tsx';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';

const Registers = () => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [businessId]);

  const {
    hasNextPage,
    fetchNextPage,
    data: registersData,
    isFetching,
    isLoading,
  } = useRegistersListQuery({
    page: 1,
    page_size: 18,
    filter_by_name: searchValue,
  });

  const registers = (registersData?.pages || []).flatMap(({ data }) => data);
  const headerAction = (
    <div className="flex max-w-[660px] flex-wrap-reverse w-full justify-end items-center gap-3">
      <SearchField
        className="w-[220px]"
        placeholder="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Button variant="secondary" className="w-fit" onClick={() => openModal(CreateRegisterModal)}>
        New label
      </Button>
    </div>
  );

  return (
    <PageLayout
      title={
        <div className="flex items-center gap-4">
          <span>Compliance Progress</span>
          {isFetching && !isLoading && (
            <Spinner className="w-5 h-5 min-w-5 min-h-5" variant="primary" />
          )}
        </div>
      }
      headerAction={headerAction}
    >
      <EmptyListState
        title="No labels created yet"
        description="A compliance label is a simple tool within the app that helps you keep track of all your company's legal, regulatory, and policy requirements. You can add questions to forms for compliance with these labels, then you can report based on these labels. Create your first label from this page."
        isLoading={isLoading}
        isSearchUsed={!!searchValue}
        itemsCount={registers.length}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center pb-6">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      {!!registers.length && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={registers.length}
          hasMore={!!hasNextPage}
          className="w-full flex flex-col space-y-2"
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          {registers.map(register => (
            <RegisterItem key={register.id} register={register} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Registers, Role.COMPLIANCE_ADMIN);
